import { useCallback } from 'react';

import qs from 'qs';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useCurrentUserId, useSessionUuid } from 'src/app/shared/hooks/session';
import type { ShareAction } from 'src/app/shared/hooks/share';
import { useAvailableShareActions } from 'src/app/shared/hooks/share';
import { shareFlyer } from 'src/app/shared/share/flyer';

import type { Context, Petition } from '../../types';

type Channel = 'flyer';
const CHANNELS: readonly Channel[] = ['flyer'];

type UseFlyerShareArgs = { petition: Petition; context: Context; content: string };

export function useFlyerShare({
	petition,
	context,
	content,
}: UseFlyerShareArgs): ShareAction<Channel> & { track: () => void } {
	const { resolveUrl } = useUtilityContext();
	const sourceLocation = useQueryParamValue('source_location');
	const sessionUuid = useSessionUuid();
	const currentUserId = useCurrentUserId();
	const track = useTracking();

	const getContent = (_channel: Channel) => '';

	const getUrl = useCallback(
		(_channel: Channel) => {
			const utmParams = qs.stringify(
				{
					utm_source: 'share_petition',
					utm_medium: 'qr_code_flyer',
					utm_campaign: 'starter_dashboard',
					utm_content: content,
					recruited_by_id: sessionUuid,
					recruiter: currentUserId,
				},
				{ encodeValuesOnly: true },
			);
			return resolveUrl(`/p/${petition.slug}?${utmParams}`);
		},
		[content, currentUserId, petition.slug, resolveUrl, sessionUuid],
	);

	const getTrackingData = useCallback(
		(_channel: Channel) => ({
			view_context: 'starter_dashboard',
			own_petition: petition.user.id === currentUserId,
			context,
			content,
			petition_id: `petition_${petition.id}`,
			source_location: sourceLocation,
		}),
		[content, context, currentUserId, petition.id, petition.user.id, sourceLocation],
	);

	const trackingEventName = 'share_petition';

	const options = { trackingEventName, getUrl, getTrackingData, getContent };

	return {
		...useAvailableShareActions(CHANNELS, options)[0],
		track: useCallback(
			() =>
				shareFlyer({ trackingData: getTrackingData('flyer'), trackingEvents: { success: trackingEventName } }, track),
			[getTrackingData, track, trackingEventName],
		),
	};
}
